import { useEffect, useState } from 'react'
import { withStyles } from '@aura/components/styles'
import Button from '@aura/components/button'
import FormHelperText from '@aura/components/form-helper-text'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useTranslation } from 'react-i18next'
import { useAuraThemeSwitcher } from '@aura/components/themes'
import Grid from '@aura/components/grid'
import Box from '@aura/components/box'
import Radio from '@aura/components/radio'
import { Typography } from '@aura/components'
import PropTypes from 'prop-types'
import CustomLoader from '../../../../components/custom-loader'
import FetchAuthenticatorStatus from '../../../../constants/fetch-authenticators'
import RegistrationContainerNew from '../../../../components/registration-container-new'
import styles from './styles'


const Component = ({
    authenticatorStatus,
    canSkip,
    classes,
    data,
    error,
    initAuthenticators,
    isProcessing,
    mfaRequired,
    onEnroll,
    onFinish,
    onNext
}) => {

    const { t: translate } = useTranslation()
    const { theme } = useAuraThemeSwitcher()
    const [ selectedValue, setSelectedValue ] = useState('')
    const [ hoveredType, setHoveredType ] = useState(null)

    useEffect(() => {
        if (initAuthenticators) {
            initAuthenticators()
        }
    }, [])

    const mfaFactors = Object.values(data).map(rec => {
        return {
            img: rec.provider === 'o' ? `/img/okta_icon_${theme.palette.mode}.svg?url` : `/img/google_icon_${theme.palette.mode}.svg?url`,
            ...rec
        }
    })

    const active = mfaFactors.filter(rec => rec.status === 'ACTIVE')
    const notSetup = mfaFactors.filter(rec => rec.status === 'NOT_SETUP')

    const noAuthenticators = !mfaFactors || mfaFactors.length === 0
    const hasActive = active.length > 0
    const hasNotSetup = notSetup.length > 0
    const isContinuing = hasActive && hasNotSetup
    const isLoading = authenticatorStatus === FetchAuthenticatorStatus.PENDING

    const handleAuthenticatorClick = factorType => {
        const factor = Object.values(data).find(rec => rec.factorType === factorType)
        onNext(factor)
    }

    const handleOnFinish = () => {
        onFinish()
    }

    const handleChange = event => {
        setSelectedValue(event.target.value)
    }

    const title = translate('mfa')
    const verbiage = <Typography>{translate('authenticators.verbiage01')}</Typography>
    const isContinuingVerbiage = <Typography>{translate('authenticators.verbiage02')}</Typography>

    const footerContent = !isLoading ? (
        <Grid className={classes.buttonContainer} container item md={12} xs={12}>
            <Grid container className={hasActive ? classes.actionRowContinue : classes.actionRow} justify={hasActive ? 'flex-end' : 'space-between'}>
                {/* Show "Finish" button when user has at least 1 active MFA */}
                {hasActive ? (
                    <Button
                        className={classes.buttonSubmit}
                        onClick={handleOnFinish}
                        variant="text"
                        disabled={isProcessing || isLoading}>{translate('finish')}
                    </Button>
                ) : (
                    <>
                        {/* Show "Setup Later" button when MFA not required and no active MFAs */}
                        {!mfaRequired ? (
                            <Button
                                className={classes.buttonSkip}
                                onClick={handleOnFinish}
                                variant="text"
                                disabled={isProcessing || isLoading}>{translate('mfaEnrollment.setupLater')}
                            </Button>
                        ) : <div />}
                        {/* Show "Next" button when user needs to select an MFA to setup */}
                        {hasNotSetup && <Button
                            className={classes.buttonSubmit}
                            disabled={!selectedValue || isProcessing || isLoading}
                            onClick={() => handleAuthenticatorClick(selectedValue)}
                            variant="contained">{translate('next')}
                        </Button>}
                    </>
                )}
            </Grid>
        </Grid>
    ) : null

    return (
        <RegistrationContainerNew footerActions={footerContent} title={title} verbiage={isContinuing ? isContinuingVerbiage : verbiage}>
            <div className={classes.fieldsContainer}>
                {isLoading ? <CustomLoader label={translate('loading')} style={{ marginTop: '40px' }}/>
                    : <>
                        {Object.values(active).map(({ desc, img, isRequired, factorType, factorName }, idx) => (
                            <Box
                                key={idx}
                                className={classes.mfaOptionActive}
                            >
                                <Box className={classes.mfaNameContainer}>
                                    <Box className={classes.mfaIconBox}>
                                        <img className={classes.mfaLogo} src={img} />
                                        <div className={classes.mfaTitle}>{translate(`authenticatorOptions.${factorName}`)} {isRequired ? '*' : ''}</div>
                                    </Box>
                                    <Typography className={classes.mfaDesc}>{translate(`authenticatorOptions.${desc}`)}</Typography>
                                </Box>
                                <Box className={classes.radioButtonIcon}>
                                    <CheckCircleIcon className={classes.checkCircle} color='success' />
                                </Box>
                            </Box>
                        ))}
                        {Object.values(notSetup).map(({ desc, img, isRequired, factorType, factorName }, idx) => (
                            <Box
                                key={idx}
                                className={`${classes.mfaOption} 
                                    ${(hoveredType === factorType || selectedValue === factorType) && classes.mfaOptionSelected}
                                `}
                                onMouseEnter={() => setHoveredType(factorType)}
                                onMouseLeave={() => setHoveredType(null)}
                                onClick={() => handleChange({ target: { value: factorType } })}
                            >
                                <Box className={classes.mfaNameContainer}>
                                    <Box className={classes.mfaIconBox}>
                                        <img className={classes.mfaLogo} src={img} />
                                        <div className={classes.mfaTitle}>{translate(`authenticatorOptions.${factorName}`)} {isRequired ? '*' : ''}</div>
                                    </Box>
                                    <Typography className={classes.mfaDesc}>{translate(`authenticatorOptions.${desc}`)}</Typography>
                                </Box>
                                <Box className={classes.radioButton}>
                                    { isContinuing ? <Button
                                        className={classes.buttonSkip}
                                        onClick={() => handleAuthenticatorClick(factorType)}
                                        variant="text"
                                        disabled={isProcessing}>{translate('setup')}
                                    </Button> : <Radio
                                        className={classes.radio}
                                        checked={selectedValue === factorType}
                                        onChange={handleChange}
                                        value={factorType}
                                        name="radio-buttons"
                                        inputProps={{ 'aria-label': `${factorType}` }}
                                    />}
                                </Box>
                            </Box>
                        ))}
                    </>}
            </div>
            <FormHelperText error className={classes.errorText}>{error && translate('oktaApiResponse.' + `${error}`)}</FormHelperText>
            <Box className={classes.warningLine}>{translate('mfaEnrollment.verbiage07')} </Box>
        </RegistrationContainerNew>
    )
}

Component.displayName = 'Authenticators'

Component.defaultProps = {
    data: {}
}

Component.propTypes = {
    authenticatorStatus: PropTypes.string,
    canSkip: PropTypes.bool,
    classes: PropTypes.shape({
        actionRow: PropTypes.object,
        button: PropTypes.object,
        buttonContainer: PropTypes.object,
        contentDivider: PropTypes.object,
        fieldsContainer: PropTypes.object,
        loader: PropTypes.object,
        mfaListContainer: PropTypes.object,
        mfaLogo: PropTypes.object,
        mfaLogoContainer: PropTypes.object,
        mfaSetupTitle: PropTypes.object,
    }),
    data: PropTypes.object,
    error: PropTypes.string,
    initAuthenticators: PropTypes.func,
    isProcessing: PropTypes.bool,
    mfaRequired: PropTypes.bool,
    onEnroll: PropTypes.func,
    onFinish: PropTypes.func,
    onNext: PropTypes.func
}

export default withStyles(Component, styles)
